import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export interface TenantBranding {
  id: string;
  tenant_id: string;
  custom_domain?: string;
  app_name?: string;
  logo_header_url?: string;
  logo_footer_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  background_color?: string;
  text_color?: string;
  custom_css?: string;
  footer_text?: string;
  footer_links?: Array<{name: string, url: string}>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useTenantBranding(tenantId?: string) {
  const { user } = useAuth();
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchBranding = async (targetTenantId?: string) => {
    const searchTenantId = targetTenantId || tenantId;
    if (!searchTenantId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tenant_branding')
        .select('*')
        .eq('tenant_id', searchTenantId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data) {
        // Parse footer_links if it's a JSON string
        const parsedData = {
          ...data,
          footer_links: data.footer_links ? 
            (Array.isArray(data.footer_links) ? data.footer_links : JSON.parse(data.footer_links as string)) 
            : []
        };
        setBranding(parsedData as TenantBranding);
      } else {
        setBranding(null);
      }
    } catch (error) {
      console.error('Error fetching tenant branding:', error);
      // Don't show toast error for missing branding - it's expected for many cases
    } finally {
      setLoading(false);
    }
  };

  const saveBranding = async (brandingData: Partial<TenantBranding>) => {
    if (!tenantId || !user) return false;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('tenant_branding')
        .upsert({
          tenant_id: tenantId,
          ...brandingData,
        }, {
          onConflict: 'tenant_id'
        });

      if (error) throw error;

      toast.success('Branding-Einstellungen gespeichert');
      await fetchBranding();
      return true;
    } catch (error) {
      console.error('Error saving tenant branding:', error);
      toast.error('Fehler beim Speichern der Branding-Einstellungen');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (file: File, assetType: 'logo_header' | 'logo_footer' | 'favicon') => {
    if (!tenantId) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${tenantId}/${assetType}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('tenant-branding')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('tenant-branding')
        .getPublicUrl(fileName);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading asset:', error);
      toast.error('Fehler beim Upload des Assets');
      return null;
    }
  };

  const getBrandingByDomain = async (domain: string): Promise<TenantBranding | null> => {
    try {
      const { data, error } = await supabase
        .from('tenant_branding')
        .select('*')
        .eq('custom_domain', domain)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data) {
        // Parse footer_links if it's a JSON string
        const parsedData = {
          ...data,
          footer_links: data.footer_links ? 
            (Array.isArray(data.footer_links) ? data.footer_links : JSON.parse(data.footer_links as string)) 
            : []
        };
        return parsedData as TenantBranding;
      }
      return null;
    } catch (error) {
      console.error('Error fetching branding by domain:', error);
      return null;
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchBranding();
    }
  }, [tenantId]);

  return {
    branding,
    loading,
    saving,
    fetchBranding,
    saveBranding,
    uploadAsset,
    getBrandingByDomain,
  };
}